import { supabase } from '../supabase';
import { normalize } from './importParsing';

/**
 * Listas de precio de costo por proveedor.
 *
 * Es una capa NUEVA y separada del costo único del producto
 * (`products.cost_price`): guardar una lista acá no cambia ningún costo del
 * sistema. La conexión entre la lista y el costo efectivo la hace el
 * requerimiento del criterio de costo configurable, que es aparte.
 *
 * Ojo con el nombre: `src/utils/priceLists.js` son listas de VENTA. No
 * confundir ni mezclar semánticas.
 */

export const MONEDAS = ['ARS', 'USD', 'EUR'];

export const fetchProviderLists = async (
  organizationId,
  { proveedorId = null, soloActivas = false } = {}
) => {
  if (!organizationId) return [];

  let query = supabase
    .from('proveedor_listas_precio')
    .select('*')
    .eq('organization_id', organizationId)
    .order('vigencia_desde', { ascending: false })
    .order('created_at', { ascending: false });

  if (proveedorId) query = query.eq('proveedor_id', proveedorId);
  if (soloActivas) query = query.eq('is_active', true).is('anulada_at', null);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const fetchProviderPrices = async (
  organizationId,
  { listaId = null, proveedorId = null, productId = null, soloVigentes = true } = {}
) => {
  if (!organizationId) return [];

  let query = supabase
    .from('proveedor_precios')
    .select('*')
    .eq('organization_id', organizationId);

  if (listaId) query = query.eq('lista_id', listaId);
  if (proveedorId) query = query.eq('proveedor_id', proveedorId);
  if (productId) query = query.eq('product_id', productId);
  if (soloVigentes) query = query.eq('vigente', true);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Crea la cabecera de una lista y, si el proveedor ya tenía una vigente,
 * la cierra: para un mismo producto sólo una línea cuenta.
 *
 * Devuelve { lista, listaAnteriorId } — el id de la anterior se guarda para
 * que el deshacer pueda reabrirla.
 */
export const crearLista = async ({
  organizationId,
  proveedorId,
  nombre,
  moneda = 'ARS',
  tipoCambioSnapshot = null,
  vigenciaDesde = null,
  fuente = 'excel',
  archivoOrigen = null,
  cerrarAnterior = true,
}) => {
  if (!organizationId) throw new Error('Falta la organización');
  if (!proveedorId) throw new Error('Elegí el proveedor de la lista');

  const desde = vigenciaDesde || new Date().toISOString().slice(0, 10);
  let listaAnteriorId = null;

  if (cerrarAnterior) {
    const vigentes = await fetchProviderLists(organizationId, {
      proveedorId,
      soloActivas: true,
    });
    const anterior = vigentes.find((lista) => !lista.vigencia_hasta);

    if (anterior) {
      listaAnteriorId = anterior.id;
      const { error: cierreError } = await supabase
        .from('proveedor_listas_precio')
        .update({ vigencia_hasta: desde, updated_at: new Date().toISOString() })
        .eq('id', anterior.id)
        .eq('organization_id', organizationId);

      if (cierreError) throw cierreError;
    }
  }

  const { data, error } = await supabase
    .from('proveedor_listas_precio')
    .insert([
      {
        organization_id: organizationId,
        proveedor_id: proveedorId,
        nombre,
        moneda,
        tipo_cambio_snapshot: tipoCambioSnapshot,
        vigencia_desde: desde,
        fuente,
        archivo_origen: archivoOrigen,
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return { lista: data, listaAnteriorId };
};

/**
 * Renombra una lista. Sólo el nombre: moneda y vigencia definen cómo se leen
 * los precios cargados, así que cambiarlas exige importar una lista nueva.
 */
export const renombrarLista = async (organizationId, listaId, nombre) => {
  const limpio = String(nombre || '').trim();
  if (!limpio) throw new Error('El nombre de la lista es obligatorio');

  const { data, error } = await supabase
    .from('proveedor_listas_precio')
    .update({ nombre: limpio, updated_at: new Date().toISOString() })
    .eq('id', listaId)
    .eq('organization_id', organizationId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Inserta o actualiza las líneas de una lista.
 *
 * Es idempotente por el índice único (lista, producto, variante): reimportar
 * el mismo archivo actualiza las líneas, no las duplica.
 */
export const upsertPricesFromImport = async (organizationId, listaId, proveedorId, filas) => {
  if (!organizationId || !listaId) throw new Error('Falta la lista destino');
  const limpias = (filas || []).filter((fila) => fila?.product_id);
  if (limpias.length === 0) return 0;

  const rows = limpias.map((fila) => ({
    organization_id: organizationId,
    lista_id: listaId,
    proveedor_id: proveedorId,
    product_id: fila.product_id,
    variant_id: fila.variant_id || null,
    codigo_proveedor: fila.codigo_proveedor || null,
    descripcion_origen: fila.descripcion_origen || null,
    costo: Number(fila.costo || 0),
    moneda: fila.moneda || 'ARS',
    cantidad_minima: fila.cantidad_minima ?? null,
    bulto: fila.bulto ?? null,
    vigente: true,
    updated_at: new Date().toISOString(),
  }));

  // El índice único usa coalesce(variant_id, uuid-cero), que onConflict no
  // puede nombrar: se insertan por tandas y se resuelve el choque puntual.
  const TANDA = 500;
  let insertadas = 0;

  for (let i = 0; i < rows.length; i += TANDA) {
    const tanda = rows.slice(i, i + TANDA);
    const { error } = await supabase.from('proveedor_precios').insert(tanda);

    if (error) {
      // Reintento fila por fila: la lista puede tener líneas previas de una
      // carga anterior a la misma lista (edición manual + reimport).
      for (const row of tanda) {
        const { error: filaError } = await supabase.from('proveedor_precios').insert([row]);
        if (!filaError) {
          insertadas += 1;
          continue;
        }

        const { error: updateError } = await supabase
          .from('proveedor_precios')
          .update({
            costo: row.costo,
            moneda: row.moneda,
            codigo_proveedor: row.codigo_proveedor,
            descripcion_origen: row.descripcion_origen,
            cantidad_minima: row.cantidad_minima,
            bulto: row.bulto,
            vigente: true,
            updated_at: row.updated_at,
          })
          .eq('lista_id', row.lista_id)
          .eq('product_id', row.product_id)
          .eq('organization_id', organizationId);

        if (updateError) throw updateError;
        insertadas += 1;
      }
      continue;
    }

    insertadas += tanda.length;
  }

  return insertadas;
};

/**
 * Alias aprendidos: proveedor + código del proveedor → producto.
 * Es lo que hace que la segunda importación del mismo proveedor entre sola.
 */
export const fetchAliases = async (organizationId, proveedorId) => {
  if (!organizationId || !proveedorId) return {};

  const { data, error } = await supabase
    .from('proveedor_precio_alias')
    .select('codigo_proveedor, product_id, variant_id')
    .eq('organization_id', organizationId)
    .eq('proveedor_id', proveedorId);

  if (error) throw error;

  return (data || []).reduce((acc, alias) => {
    const clave = normalize(alias.codigo_proveedor);
    if (clave) acc[clave] = { product_id: alias.product_id, variant_id: alias.variant_id };
    return acc;
  }, {});
};

export const saveAliases = async (organizationId, proveedorId, pares) => {
  const limpios = (pares || []).filter((par) => par?.codigo_proveedor && par?.product_id);
  if (!organizationId || !proveedorId || limpios.length === 0) return 0;

  const rows = limpios.map((par) => ({
    organization_id: organizationId,
    proveedor_id: proveedorId,
    codigo_proveedor: String(par.codigo_proveedor).trim(),
    descripcion_origen_normalizada: par.descripcion_origen
      ? normalize(par.descripcion_origen)
      : null,
    product_id: par.product_id,
    variant_id: par.variant_id || null,
  }));

  const { error } = await supabase
    .from('proveedor_precio_alias')
    .upsert(rows, { onConflict: 'organization_id,proveedor_id,codigo_proveedor' });

  if (error) throw error;
  return rows.length;
};

/**
 * Matching de una fila del archivo contra el catálogo.
 *
 * Orden: alias guardado > código de barras exacto > nombre normalizado.
 * Lo que no matchea vuelve sin producto, para que el usuario lo resuelva a
 * mano en la previsualización (y esa resolución se guarde como alias).
 */
export const matchearFila = (fila, { aliases = {}, porBarcode = {}, porNombre = {} }) => {
  const codigo = normalize(fila?.codigo_proveedor);
  if (codigo && aliases[codigo]) {
    return { ...aliases[codigo], origen: 'alias' };
  }

  const barcode = normalizarBarcode(fila?.codigo_proveedor);
  if (barcode && porBarcode[barcode]) {
    return { product_id: porBarcode[barcode], variant_id: null, origen: 'barcode' };
  }

  const nombre = normalize(fila?.descripcion_origen);
  if (nombre && porNombre[nombre]) {
    return { product_id: porNombre[nombre], variant_id: null, origen: 'nombre' };
  }

  return { product_id: null, variant_id: null, origen: 'sin_match' };
};

/**
 * Normalización de códigos de barras, igual que en el importador de costos:
 * sin acentos, sin signos y sin espacios. Un "779 123456" del archivo del
 * proveedor tiene que matchear con "779123456" del catálogo.
 */
export const normalizarBarcode = (valor) => normalize(String(valor || '')).replaceAll(' ', '');

/** Índices de búsqueda del catálogo, para no recorrer el array por cada fila. */
export const construirIndicesCatalogo = (productos) => {
  const porBarcode = {};
  const porNombre = {};

  (productos || []).forEach((producto) => {
    const barcode = normalizarBarcode(producto?.barcode);
    if (barcode && !porBarcode[barcode]) porBarcode[barcode] = producto.id;

    const nombre = normalize(producto?.name);
    if (nombre && !porNombre[nombre]) porNombre[nombre] = producto.id;
  });

  return { porBarcode, porNombre };
};

/**
 * ¿Está vigente esta lista hoy? Activa, no anulada y dentro del rango de fechas.
 */
export const listaVigente = (lista, hoy = new Date()) => {
  if (!lista || lista.is_active === false || lista.anulada_at) return false;

  const fecha = hoy.toISOString().slice(0, 10);
  if (lista.vigencia_desde && lista.vigencia_desde > fecha) return false;
  if (lista.vigencia_hasta && lista.vigencia_hasta < fecha) return false;
  return true;
};

/**
 * Convierte a moneda base para poder comparar peras con peras.
 *
 * La cotización es la GLOBAL de la organización, no la de cada lista: si cada
 * lista usara la suya, dos listas cargadas en semanas distintas quedarían
 * comparadas con tasas distintas y el "más barato" mentiría.
 */
export const aMonedaBase = (costo, moneda, usdRate) => {
  const valor = Number(costo || 0);
  if (moneda === 'USD') {
    const tasa = Number(usdRate || 0);
    return tasa > 0 ? valor * tasa : null; // sin cotización no se puede comparar
  }
  return valor;
};

/**
 * Comparativa de precios por producto entre proveedores.
 *
 * Es de SOLO LECTURA: mirar quién vende más barato no cambia ningún costo del
 * sistema. Que el costo del producto pase a salir de acá es el requerimiento
 * del criterio de costo configurable, que es aparte.
 *
 * Devuelve, por producto: las ofertas ordenadas de menor a mayor, cuál es la
 * más barata, y cuánto se ahorra contra la más cara.
 */
export const compararPrecios = async (organizationId, { usdRate = 0 } = {}) => {
  if (!organizationId) return [];

  const [listas, precios] = await Promise.all([
    fetchProviderLists(organizationId, {}),
    fetchProviderPrices(organizationId, { soloVigentes: true }),
  ]);

  const listasPorId = new Map(listas.map((lista) => [lista.id, lista]));
  const porProducto = new Map();

  precios.forEach((precio) => {
    const lista = listasPorId.get(precio.lista_id);
    if (!listaVigente(lista)) return;

    const costoBase = aMonedaBase(precio.costo, precio.moneda, usdRate);
    if (costoBase === null || costoBase <= 0) return;

    const oferta = {
      proveedor_id: precio.proveedor_id,
      lista_id: precio.lista_id,
      lista_nombre: lista?.nombre || '',
      costo: Number(precio.costo || 0),
      costoBase,
      moneda: precio.moneda,
      bulto: precio.bulto,
      codigo_proveedor: precio.codigo_proveedor,
      vigencia_desde: lista?.vigencia_desde || null,
    };

    const actual = porProducto.get(precio.product_id) || [];
    actual.push(oferta);
    porProducto.set(precio.product_id, actual);
  });

  return [...porProducto.entries()].map(([productId, ofertas]) => {
    const ordenadas = [...ofertas].sort((a, b) => a.costoBase - b.costoBase);
    const masBarata = ordenadas[0];
    const masCara = ordenadas[ordenadas.length - 1];
    const ahorro = masCara.costoBase - masBarata.costoBase;

    return {
      product_id: productId,
      ofertas: ordenadas,
      proveedores: ordenadas.length,
      masBarata,
      masCara,
      ahorro,
      ahorroPorcentaje: masCara.costoBase > 0 ? (ahorro / masCara.costoBase) * 100 : 0,
    };
  });
};
